import { goalKey } from "../app/goals/goalRules";

const DAY_MS = 86400000;
const atStart = (y, m, d) => new Date(y, m, d, 0, 0, 0, 0);
const atEnd = (y, m, d) => new Date(y, m, d, 23, 59, 59, 999);
/** Yerel günler arası gün sayısı (yaz saati kaymasından etkilenmez). */
const dayIndex = (date) =>
  Math.round(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / DAY_MS);

/**
 * Kıyaslanacak önceki dönem: aynı uzunlukta. Süren hafta geçen haftanın
 * aynı günleriyle, süren ay geçen ayın aynı günleriyle kıyaslanır; yoksa
 * hafta ortasında herkes geride görünürdü. Takvim aralığı (key null) ve
 * diğerleri hemen öncesindeki eşit uzunluktaki aralıkla (mobil ile aynı).
 */
export function previousPeriodRange(key, start, end) {
  const y = start.getFullYear();
  const m = start.getMonth();
  const d = start.getDate();
  const length = dayIndex(end) - dayIndex(start) + 1;

  if (key === "thisWeek") {
    return { start: atStart(y, m, d - 7), end: atEnd(y, m, d - 7 + length - 1) };
  }
  if (key === "thisMonth" || key === "lastMonth") {
    // Önceki ayın başı; süren ayda aynı gün sayısı, önceki ayın sonunda kesilir.
    const prevLast = new Date(y, m, 0).getDate();
    const days = key === "lastMonth" ? prevLast : Math.min(length, prevLast);
    return { start: atStart(y, m - 1, 1), end: atEnd(y, m - 1, days) };
  }
  return { start: atStart(y, m, d - length), end: atEnd(y, m, d - 1) };
}

/** Puan farkı (tamamlama oranı); önceki yoksa null. */
export function rateDelta(current, previous) {
  if (previous === undefined || previous === null || !Number.isFinite(previous)) return null;
  return Math.round(current - previous);
}

/** Üyenin tamamlama oranı: hedeflerinin ortalaması, her hedef 0-100 arası. */
export function memberCompletionRate(member) {
  const rates = (member?.labelStats ?? []).map((stat) => {
    const raw = Number(stat?.completionRate);
    return Number.isFinite(raw) ? Math.min(100, Math.max(0, raw)) : 0;
  });
  if (rates.length === 0) return 0;
  return Math.floor(rates.reduce((total, rate) => total + rate, 0) / rates.length);
}

/** Grup ortalaması (yuvarlanmış). */
export function groupAverage(rates) {
  if (rates.length === 0) return 0;
  return Math.round(rates.reduce((total, rate) => total + rate, 0) / rates.length);
}

const clampRate = (raw) => Math.round(Math.min(100, Math.max(0, raw)));

/**
 * Üyenin dönem oranı: yeni sunucunun periodRate'i, eski sunucuda
 * completionRate. Çubuk, sıralama ve fark hep bu 0..100 tam sayıdan.
 */
export function memberRate(member) {
  const raw = Number(member?.periodRate ?? member?.completionRate);
  return Number.isFinite(raw) ? clampRate(raw) : 0;
}

/**
 * Üyenin önceki döneme göre farkı: sunucu delta verdiyse o (null, önceki
 * dönem yok demek); eski sunucuda önceki dönemin oranından hesaplanır.
 */
export function memberDelta(member, previous) {
  if (member && member.delta !== undefined) return member.delta;
  const prev = previous === undefined || previous === null ? previous : clampRate(Number(previous));
  return rateDelta(memberRate(member), prev);
}

/** Sunucu üyelerde önceki dönem oranını veriyor mu (yeni sunucu). */
export function hasServerCompare(members) {
  return Array.isArray(members) && members.some((m) => m && m.previousRate !== undefined);
}

/**
 * Grup ortalaması ve farkı: sunucunun average / averageDelta alanları önce;
 * yoksa üyelerin previousRate'inden; o da yoksa (eski sunucu) önceki dönem
 * listesinden.
 */
export function groupCompare({ average, averageDelta, members = [], previousMembers } = {}) {
  const list = Array.isArray(members) ? members : [];
  const avg = Number.isFinite(average) ? average : groupAverage(list.map(memberRate));
  if (averageDelta !== undefined) return { average: avg, averageDelta };
  if (hasServerCompare(list)) {
    const prevRates = list.map((m) => m.previousRate).filter((r) => r !== null && r !== undefined);
    return { average: avg, averageDelta: prevRates.length ? rateDelta(avg, groupAverage(prevRates)) : null };
  }
  if (previousMembers?.length && list.length) {
    return { average: avg, averageDelta: rateDelta(avg, groupAverage(previousMembers.map(memberRate))) };
  }
  return { average: avg, averageDelta: null };
}

/** Tek hedefin oranı: sunucunun completionRate'i, yoksa değer / hedef; 0..100. */
function goalRate(stat) {
  const raw = Number(stat?.completionRate);
  if (Number.isFinite(raw)) return Math.min(100, Math.max(0, raw));
  const goalValue = Number(stat?.goalValue) || 0;
  return goalValue > 0 ? Math.min(100, Math.max(0, ((Number(stat?.value) || 0) / goalValue) * 100)) : 0;
}

/**
 * Hedef bazında döküm: hedef kimliği ad + biçim (goalKey); aynı ad farklı
 * biçim ayrı satır kalır ve adına biçim eklenir ("Kuran · Ezber"). Oran
 * üyelerin hedef oranlarının ortalaması, tam sayı. activityName: biçimin
 * görünen adı (çeviri).
 */
export function aggregateGoals(userStats, activityName = (activity) => activity) {
  const map = new Map();
  (userStats || []).forEach((u) => {
    (u?.labelStats || []).forEach((ls) => {
      const key = ls.key ?? goalKey(ls.label, ls.activity);
      const cur = map.get(key) || {
        key,
        label: ls.label,
        activity: ls.activity || "okuma",
        unit: ls.unit ?? null,
        value: 0,
        goalValue: 0,
        rateSum: 0,
        count: 0,
      };
      cur.value += ls.value || 0;
      cur.goalValue += ls.goalValue || 0;
      cur.rateSum += goalRate(ls);
      cur.count += 1;
      map.set(key, cur);
    });
  });
  const rows = Array.from(map.values());
  const labelCount = new Map();
  rows.forEach((r) => labelCount.set(r.label, (labelCount.get(r.label) || 0) + 1));
  return rows
    .map(({ rateSum, count, ...r }) => ({
      ...r,
      name: labelCount.get(r.label) > 1 ? `${r.label} · ${activityName(r.activity)}` : r.label,
      completion: count ? Math.round(rateSum / count) : 0,
    }))
    .sort((a, b) => b.completion - a.completion);
}

/** Kıyasın hangi döneme göre olduğu. */
export function compareLabel(key) {
  switch (key) {
    case "today":
      return "düne göre";
    case "yesterday":
      return "önceki güne göre";
    case "thisWeek":
      return "geçen haftaya göre";
    case "lastWeek":
      return "önceki haftaya göre";
    case "thisMonth":
      return "geçen aya göre";
    case "lastMonth":
      return "önceki aya göre";
    default:
      return "önceki döneme göre";
  }
}
